import { memo, useState, useCallback, useEffect } from 'react';
import { cn, Gallery, NavDots } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import Img from '@UI/Img';
import NavSideArrows from '@Components/NavSideArrows';
import Sign from '../Sign';
import styles from './FullscreenSlider.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface MaediaData {
  img: string;
  sign: string;
}

export interface FullscreenSliderProps extends HTMLAttributes<HTMLDivElement> {
  medias: MaediaData[];
  className?: string;
}

const FullscreenSlider: FC<FullscreenSliderProps> = (props) => {
  const { medias, className, ...restProps } = props;
  const { isMobile, isMobileM } = useMedias();
  const [slide, setSlide] = useState(0);
  const numberOfSlides = medias.length;

  const handleChangeCurrent = useCallback(({ current }) => {
    setSlide(current);
  }, []);

  const handleChangeSlide = useCallback((_e, index) => {
    setSlide(index);
  }, []);

  const normalizeSlide = useCallback(
    (value: number) => {
      if (value < 0) return numberOfSlides - 1;
      if (value > numberOfSlides - 1) return 0;

      return value;
    },
    [numberOfSlides],
  );

  const handlePrev = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev - 1));
  }, [normalizeSlide]);

  const handleNext = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev + 1));
  }, [normalizeSlide]);

  useEffect(() => {
    if (typeof slide !== 'number') return;

    setSlide(slide);
  }, [slide]);

  return (
    <div {...restProps} className={cn(styles.wrapper, className)}>
      <div className={styles.gallery}>
        {!isMobileM && numberOfSlides > 1 && (
          <div>
            <NavSideArrows onPrev={handlePrev} onNext={handleNext} />
          </div>
        )}
        <Gallery slideIndex={slide} onChangeCurrent={handleChangeCurrent}>
          {medias.map((media) => (
            <div className={styles.mediaWrapper} key={media.img}>
              <Img className={styles.media} src={media.img} />
              {isMobile && media.sign && <Sign className={styles.sign}>{media.sign}</Sign>}
            </div>
          ))}
        </Gallery>
      </div>

      <div className={styles.container}>
        <div className={styles.wrapperDots}>
          {!isMobile && <Sign className={styles.sign}>{medias[slide]?.sign}</Sign>}
          {numberOfSlides > 1 && (
            <NavDots
              count={numberOfSlides}
              defaultIndex={slide}
              onChangeSlide={handleChangeSlide}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(FullscreenSlider);
